import { useState, useEffect, useRef } from "react";
import { useGeneralCreativeUpload } from "./useGeneralCreativeUpload";
import { useLocationCreativeUpload } from "./useLocationCreativeUpload";
import { useCampaignStorage } from "@/hooks/campaign/utils/useCampaignStorage";
import type { Creative } from "../types";

export const useCreativeUpload = (initialUseLocationSpecific: boolean = false) => {
  const storage = useCampaignStorage();
  
  // Load from storage, with initialUseLocationSpecific as fallback
  const [useLocationSpecificCreatives, setUseLocationSpecificCreatives] = useState(
    storage.getUseLocationSpecificCreatives() || initialUseLocationSpecific
  );
  const [useMediaOwnerSpecificCreatives, setUseMediaOwnerSpecificCreatives] = useState(
    storage.getUseMediaOwnerSpecificCreatives()
  );
  const creativesRestoredRef = useRef(false);
  const backupTimerRef = useRef<number | null>(null);
  
  // Check for payment cancellation on mount
  useEffect(() => {
    // Check if we need to restore from backup after payment cancel
    const paymentCanceled = localStorage.getItem('paymentCanceled');
    
    if (paymentCanceled === 'true' && !creativesRestoredRef.current) {
      console.log("useCreativeUpload: Payment cancellation detected, will try to restore creative data");
      
      // Backup key pairs to check and restore if needed
      const backupPairs = [
        ['creatives', 'creativesBackup'],
        ['locationCreatives', 'locationCreativesBackup'],
        ['mediaOwnerCreatives', 'mediaOwnerCreativesBackup'],
        ['useLocationSpecificCreatives', 'useLocationSpecificCreativesBackup'],
        ['useMediaOwnerSpecificCreatives', 'useMediaOwnerSpecificCreativesBackup'],
        ['allCreativeUrls', 'allCreativeUrlsBackup'],
        ['creativeUrls', 'creativeUrlsBackup']
      ];
      
      backupPairs.forEach(([mainKey, backupKey]) => {
        const backupValue = localStorage.getItem(backupKey);
        if (backupValue && backupValue !== 'null' && backupValue !== 'undefined') {
          localStorage.setItem(mainKey, backupValue);
          console.log(`useCreativeUpload: Restored ${mainKey} from ${backupKey}`);
        }
      });
      
      creativesRestoredRef.current = true;
      
      // Set up a timer to repeatedly check and restore creative data for the next few seconds
      // This helps catch race conditions where other components might clear data
      let restorationAttempts = 0;
      const maxAttempts = 5;
      
      const checkAndRestoreInterval = setInterval(() => {
        restorationAttempts++;
        console.log(`useCreativeUpload: Restoration check attempt ${restorationAttempts}`);
        
        // Verify data is still there, restore if missing
        backupPairs.forEach(([mainKey, backupKey]) => {
          const currentValue = localStorage.getItem(mainKey);
          const backupValue = localStorage.getItem(backupKey);
          
          // If main value is missing/empty but backup exists, restore it
          if ((!currentValue || currentValue === 'null' || currentValue === 'undefined' || currentValue === '[]') && 
              backupValue && backupValue !== 'null' && backupValue !== 'undefined' && backupValue !== '[]') {
            localStorage.setItem(mainKey, backupValue);
            console.log(`useCreativeUpload: Re-restored ${mainKey} from ${backupKey}`);
          }
        });
        
        if (restorationAttempts >= maxAttempts) {
          clearInterval(checkAndRestoreInterval);
        }
      }, 500);
      
      // Clear the interval after component unmount
      return () => {
        clearInterval(checkAndRestoreInterval);
      };
    }
    
    // Start a periodic backup timer to keep backups fresh
    backupTimerRef.current = window.setInterval(() => {
      // Only perform backups if not already restored from cancellation
      if (!creativesRestoredRef.current) {
        // Get current values to back up
        const creatives = localStorage.getItem('creatives');
        const locationCreatives = localStorage.getItem('locationCreatives');
        const mediaOwnerCreatives = localStorage.getItem('mediaOwnerCreatives');
        const creativeUrls = localStorage.getItem('creativeUrls');
        const allCreativeUrls = localStorage.getItem('allCreativeUrls');
        
        // Create backups of important values
        if (creatives && creatives !== 'null' && creatives !== 'undefined') {
          localStorage.setItem('creativesBackup', creatives);
        }
        if (locationCreatives && locationCreatives !== 'null' && locationCreatives !== 'undefined') {
          localStorage.setItem('locationCreativesBackup', locationCreatives);
        }
        if (mediaOwnerCreatives && mediaOwnerCreatives !== 'null' && mediaOwnerCreatives !== 'undefined') {
          localStorage.setItem('mediaOwnerCreativesBackup', mediaOwnerCreatives);
        }
        if (creativeUrls && creativeUrls !== 'null' && creativeUrls !== 'undefined') {
          localStorage.setItem('creativeUrlsBackup', creativeUrls);
        }
        if (allCreativeUrls && allCreativeUrls !== 'null' && allCreativeUrls !== 'undefined') {
          localStorage.setItem('allCreativeUrlsBackup', allCreativeUrls);
        }
        
        // Also back up preferences
        localStorage.setItem('useLocationSpecificCreativesBackup', useLocationSpecificCreatives.toString());
        localStorage.setItem('useMediaOwnerSpecificCreativesBackup', useMediaOwnerSpecificCreatives.toString());
      }
    }, 5000);
    
    return () => {
      if (backupTimerRef.current !== null) {
        window.clearInterval(backupTimerRef.current);
      }
    };
  }, []);
  
  // Listen for payment cancellation events to create fresh backups
  useEffect(() => {
    const handlePaymentCancel = (event: Event) => {
      console.log("useCreativeUpload: Payment cancellation detected, creating fresh backups");
      
      // Get all current creative data
      const creatives = localStorage.getItem('creatives');
      const locationCreatives = localStorage.getItem('locationCreatives');
      const mediaOwnerCreatives = localStorage.getItem('mediaOwnerCreatives');
      const creativeUrls = localStorage.getItem('creativeUrls');
      const allCreativeUrls = localStorage.getItem('allCreativeUrls');
      
      // Create backups of important values
      if (creatives && creatives !== 'null' && creatives !== 'undefined') {
        localStorage.setItem('creativesBackup', creatives);
      }
      if (locationCreatives && locationCreatives !== 'null' && locationCreatives !== 'undefined') {
        localStorage.setItem('locationCreativesBackup', locationCreatives);
      }
      if (mediaOwnerCreatives && mediaOwnerCreatives !== 'null' && mediaOwnerCreatives !== 'undefined') {
        localStorage.setItem('mediaOwnerCreativesBackup', mediaOwnerCreatives);
      }
      if (creativeUrls && creativeUrls !== 'null' && creativeUrls !== 'undefined') {
        localStorage.setItem('creativeUrlsBackup', creativeUrls);
      }
      if (allCreativeUrls && allCreativeUrls !== 'null' && allCreativeUrls !== 'undefined') {
        localStorage.setItem('allCreativeUrlsBackup', allCreativeUrls);
      }
      
      // Also backup preferences
      localStorage.setItem('useLocationSpecificCreativesBackup', useLocationSpecificCreatives.toString());
      localStorage.setItem('useMediaOwnerSpecificCreativesBackup', useMediaOwnerSpecificCreatives.toString());
      
      // Set a flag for restoration on next mount/update
      creativesRestoredRef.current = false;
    };
    
    // Listen for both standard and confirmed payment cancel events
    document.addEventListener('payment-cancel', handlePaymentCancel);
    document.addEventListener('payment-cancel-confirmed', handlePaymentCancel);
    
    return () => {
      document.removeEventListener('payment-cancel', handlePaymentCancel);
      document.removeEventListener('payment-cancel-confirmed', handlePaymentCancel);
    };
  }, [useLocationSpecificCreatives, useMediaOwnerSpecificCreatives]);
  
  // Update storage when preferences change
  useEffect(() => {
    storage.setUseLocationSpecificCreatives(useLocationSpecificCreatives);
    storage.setUseMediaOwnerSpecificCreatives(useMediaOwnerSpecificCreatives);
    
    // Also backup these preferences to localStorage for legacy support
    localStorage.setItem('useLocationSpecificCreativesBackup', useLocationSpecificCreatives.toString());
    localStorage.setItem('useMediaOwnerSpecificCreativesBackup', useMediaOwnerSpecificCreatives.toString());
  }, [useLocationSpecificCreatives, useMediaOwnerSpecificCreatives, storage]);
  
  const {
    creatives,
    isUploading: generalIsUploading,
    uploadProgress,
    fileInputRef,
    handleFileChange,
    removeCreative,
    handleAreaClick,
    handleUrlSubmit
  } = useGeneralCreativeUpload();

  const {
    locationCreatives,
    mediaOwnerCreatives,
    isUploading: locationIsUploading,
    handleLocationFileChange,
    handleMediaOwnerFileChange,
    removeLocationCreative,
    removeMediaOwnerCreative,
    handleLocationUrlSubmit,
    handleMediaOwnerUrlSubmit,
    updateLocationCreativeDays,
    updateMediaOwnerCreativeDays
  } = useLocationCreativeUpload();

  // Determine overall uploading state
  const isUploading = generalIsUploading || locationIsUploading;

  return {
    creatives,
    locationCreatives,
    mediaOwnerCreatives,
    isUploading,
    uploadProgress,
    fileInputRef,
    handleFileChange,
    handleLocationFileChange,
    handleMediaOwnerFileChange,
    removeCreative,
    removeLocationCreative,
    removeMediaOwnerCreative,
    handleAreaClick,
    handleUrlSubmit,
    handleLocationUrlSubmit,
    handleMediaOwnerUrlSubmit,
    updateLocationCreativeDays,
    updateMediaOwnerCreativeDays,
    useLocationSpecificCreatives,
    setUseLocationSpecificCreatives,
    useMediaOwnerSpecificCreatives,
    setUseMediaOwnerSpecificCreatives
  };
};
